import React, { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

const translations = {
  en: {
    // Navigation
    home: "Home",
    login: "Login",
    signup: "Sign Up",
    dashboard: "Dashboard",
    logout: "Logout",
    
    // Homepage
    heroTitle: "Create Stunning Business Cards",
    heroSubtitle: "Design professional, high-definition business cards with 3D preview, QR codes, and instant PDF generation.",
    getStarted: "Get Started Free",
    tryWithoutAccount: "Try Without Account",
    
    // Features
    professionalCards: "Professional Business Cards",
    professionalCardsDesc: "Create stunning, high-definition business cards ready for printing with industry-standard measurements.",
    designCatalog: "Design Catalog",
    designCatalogDesc: "Choose from our extensive catalog of professional templates and backgrounds designed by experts.",
    preview3D: "3D Preview",
    preview3DDesc: "View your business card in stunning 3D on any device with multiple preview options.",
    instantPDF: "Instant PDF Generation",
    instantPDFDesc: "Download print-ready PDF files with perfect measurements for professional printing.",
    qrIntegration: "QR Code Integration", 
    qrIntegrationDesc: "Automatic QR code generation with all your contact information for easy sharing.",
    multiplePayment: "Multiple Payment Options",
    multiplePaymentDesc: "Accept payments via Stripe, PayPal, Square, and Zelle for maximum convenience.",
    
    // Forms
    companyName: "Company Name",
    personalName: "Personal Name",
    jobTitle: "Job Title",
    email: "Email",
    phone: "Phone Number",
    website: "Website",
    address: "Business Address",
    slogan: "Company Slogan/Tagline",
    
    // Buttons
    save: "Save",
    cancel: "Cancel",
    continue: "Continue",
    create: "Create",
    update: "Update",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    download: "Download",
    upload: "Upload",
    
    // MLM
    resellerProgram: "Reseller Program",
    upgradeToReseller: "Upgrade to Reseller",
    commission: "Commission",
    teamSize: "Team Size",
    totalEarnings: "Total Earnings",
    thisMonth: "This Month",
    pendingPayout: "Pending Payout",
    level: "Level",
    
    // Chat
    chatSupport: "Chat Support",
    aiAssistant: "AI Assistant",
    typeMessage: "Type your message...",
    connectHuman: "Connect to Human Agent",
    
    // Admin
    adminDashboard: "Admin Dashboard",
    systemOverview: "System Overview",
    totalRevenue: "Total Revenue",
    activeResellers: "Active Resellers",
    cardsGenerated: "Cards Generated",
    supportTickets: "Support Tickets",
    
    // Common
    loading: "Loading...",
    error: "Error",
    success: "Success",
    warning: "Warning",
    info: "Information",
    yes: "Yes",
    no: "No",
    ok: "OK",
    back: "Back",
    next: "Next",
    previous: "Previous",
    search: "Search",
    filter: "Filter",
    sort: "Sort",
    
    // Status
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    completed: "Completed",
    processing: "Processing",
    cancelled: "Cancelled",
    draft: "Draft",
    published: "Published",
    
    // Time
    today: "Today",
    yesterday: "Yesterday",
    thisWeek: "This Week",
    thisMonth: "This Month",
    thisYear: "This Year",
    
    // Payment
    payment: "Payment",
    paymentMethod: "Payment Method",
    creditCard: "Credit/Debit Card",
    paypal: "PayPal",
    stripe: "Stripe",
    square: "Square",
    zelle: "Zelle",
    overThePhone: "Over-the-Phone",
    
    // Validation
    required: "This field is required",
    invalidEmail: "Please enter a valid email address",
    passwordTooShort: "Password must be at least 8 characters",
    passwordsDontMatch: "Passwords do not match"
  },
  es: {
    // Navigation
    home: "Inicio",
    login: "Iniciar Sesión",
    signup: "Registrarse",
    dashboard: "Panel",
    logout: "Cerrar Sesión",
    
    // Homepage
    heroTitle: "Crea Tarjetas de Presentación Impresionantes",
    heroSubtitle: "Diseña tarjetas de presentación profesionales de alta definición con vista previa 3D, códigos QR y generación instantánea de PDF.",
    getStarted: "Comenzar Gratis",
    tryWithoutAccount: "Probar Sin Cuenta",
    
    // Features
    professionalCards: "Tarjetas de Presentación Profesionales",
    professionalCardsDesc: "Crea tarjetas de presentación impresionantes y de alta definición listas para imprimir con medidas estándar de la industria.",
    designCatalog: "Catálogo de Diseños",
    designCatalogDesc: "Elige de nuestro extenso catálogo de plantillas profesionales y fondos diseñados por expertos.",
    preview3D: "Vista Previa 3D",
    preview3DDesc: "Ve tu tarjeta de presentación en impresionante 3D en cualquier dispositivo con múltiples opciones de vista previa.",
    instantPDF: "Generación Instantánea de PDF",
    instantPDFDesc: "Descarga archivos PDF listos para imprimir con medidas perfectas para impresión profesional.",
    qrIntegration: "Integración de Código QR",
    qrIntegrationDesc: "Generación automática de código QR con toda tu información de contacto para compartir fácilmente.",
    multiplePayment: "Múltiples Opciones de Pago",
    multiplePaymentDesc: "Acepta pagos vía Stripe, PayPal, Square y Zelle para máxima conveniencia.",
    
    // Forms
    companyName: "Nombre de la Empresa",
    personalName: "Nombre Personal",
    jobTitle: "Título del Trabajo",
    email: "Correo Electrónico",
    phone: "Número de Teléfono",
    website: "Sitio Web",
    address: "Dirección Comercial",
    slogan: "Eslogan/Lema de la Empresa",
    
    // Buttons
    save: "Guardar",
    cancel: "Cancelar",
    continue: "Continuar",
    create: "Crear",
    update: "Actualizar",
    delete: "Eliminar",
    edit: "Editar",
    view: "Ver",
    download: "Descargar",
    upload: "Subir",
    
    // MLM
    resellerProgram: "Programa de Revendedores",
    upgradeToReseller: "Actualizar a Revendedor",
    commission: "Comisión",
    teamSize: "Tamaño del Equipo",
    totalEarnings: "Ganancias Totales",
    thisMonth: "Este Mes",
    pendingPayout: "Pago Pendiente",
    level: "Nivel",
    
    // Chat
    chatSupport: "Soporte por Chat",
    aiAssistant: "Asistente IA",
    typeMessage: "Escribe tu mensaje...",
    connectHuman: "Conectar con Agente Humano",
    
    // Admin
    adminDashboard: "Panel de Administrador",
    systemOverview: "Resumen del Sistema",
    totalRevenue: "Ingresos Totales",
    activeResellers: "Revendedores Activos",
    cardsGenerated: "Tarjetas Generadas",
    supportTickets: "Tickets de Soporte",
    
    // Common
    loading: "Cargando...",
    error: "Error",
    success: "Éxito",
    warning: "Advertencia",
    info: "Información",
    yes: "Sí",
    no: "No",
    ok: "OK",
    back: "Atrás",
    next: "Siguiente",
    previous: "Anterior",
    search: "Buscar",
    filter: "Filtrar",
    sort: "Ordenar",
    
    // Status
    active: "Activo",
    inactive: "Inactivo",
    pending: "Pendiente",
    completed: "Completado",
    processing: "Procesando",
    cancelled: "Cancelado",
    draft: "Borrador",
    published: "Publicado",
    
    // Time
    today: "Hoy",
    yesterday: "Ayer",
    thisWeek: "Esta Semana",
    thisMonth: "Este Mes",
    thisYear: "Este Año",
    
    // Payment
    payment: "Pago",
    paymentMethod: "Método de Pago",
    creditCard: "Tarjeta de Crédito/Débito",
    paypal: "PayPal",
    stripe: "Stripe",
    square: "Square",
    zelle: "Zelle",
    overThePhone: "Por Teléfono",
    
    // Validation
    required: "Este campo es requerido",
    invalidEmail: "Por favor ingresa un email válido",
    passwordTooShort: "La contraseña debe tener al menos 8 caracteres",
    passwordsDontMatch: "Las contraseñas no coinciden"
  }
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState("en");

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
    } else {
      // Detect browser language
      const browserLanguage = navigator.language.split("-")[0];
      if (translations[browserLanguage]) {
        setCurrentLanguage(browserLanguage);
      }
    }
  }, []);

  const changeLanguage = (language) => {
    if (translations[language]) {
      setCurrentLanguage(language);
      localStorage.setItem("language", language);
      
      // Update HTML lang attribute
      document.documentElement.lang = language;
    }
  };

  const t = (key, params = {}) => {
    let translation = translations[currentLanguage][key] || translations.en[key] || key;
    
    // Replace parameters in translation
    Object.keys(params).forEach(param => {
      translation = translation.replace(`{{${param}}}`, params[param]);
    });
    
    return translation;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    availableLanguages: Object.keys(translations)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};